/**
 * Stable per-room identity colours (design-brief-2027 Part B, owner decision
 * 2026-11-04): teal → indigo → rose → slate-green, cycled by room index.
 * Zamzi orange is deliberately EXCLUDED — orange means "your action" (the one
 * filled CTA per view) and must never become a room's identity.
 *
 * Each entry carries:
 * - dot:  the colour swatch class for legends
 * - cell: the React-day-picker modifier class for that room's busy nights
 *         (opacity-100 beats the disabled cell's default dim — the night is
 *         still taken, but now legibly THIS room's)
 * - ring: the outline for the selected-row lock
 * - quietChipName: human-readable colour name for aria labels
 */

export type RoomIdentityColour = {
  key: string;
  dot: string;
  cell: string;
  ring: string;
  name: string;
};

const PALETTE: RoomIdentityColour[] = [
  {
    key: "teal",
    dot: "bg-teal-500",
    cell: "bg-teal-100 text-teal-900 font-semibold opacity-100",
    ring: "ring-teal-400",
    name: "teal",
  },
  {
    key: "indigo",
    dot: "bg-indigo-500",
    cell: "bg-indigo-100 text-indigo-900 font-semibold opacity-100",
    ring: "ring-indigo-400",
    name: "indigo",
  },
  {
    key: "rose",
    dot: "bg-rose-500",
    cell: "bg-rose-100 text-rose-900 font-semibold opacity-100",
    ring: "ring-rose-400",
    name: "rose",
  },
  {
    key: "green",
    dot: "bg-green-600",
    cell: "bg-green-100 text-green-900 font-semibold opacity-100",
    ring: "ring-green-500",
    name: "green",
  },
  {
    key: "purple",
    dot: "bg-purple-500",
    cell: "bg-purple-100 text-purple-900 font-semibold opacity-100",
    ring: "ring-purple-400",
    name: "purple",
  },
];

/** House-wide busy nights (whole-house bookings / cascade) are always slate. */
export const HOUSE_WIDE_BUSY: RoomIdentityColour = {
  key: "house-wide",
  dot: "bg-slate-500",
  cell: "bg-slate-200 text-slate-700 font-semibold opacity-100",
  ring: "ring-slate-400",
  name: "house-wide slate",
};

export function getRoomIdentityColour(roomIndex: number): RoomIdentityColour {
  const positive = ((roomIndex % PALETTE.length) + PALETTE.length) % PALETTE.length;
  return PALETTE[positive];
}

/** Modifier class name key for a room index (used by modifiersClassNames). */
export function roomBusyModifierKey(roomIndex: number): string {
  return `roomBusy-${roomIndex}`;
}
